import { useState } from 'react';
import { idr } from '@shared/format';
import { CodeBlock, Tabs } from '../../components/ui';
import { Callout, DocSection, DocTable, EndpointList, ParamTable, type Param } from '../../components/DocBlocks';
import { API_KEY_ENV, API_ORIGIN, BASE_URL } from '../../snippets';
import { DocLink, PageHead, pick, pretty, round2, useDocs } from './context';

const LANG3 = [
  { id: 'curl', label: 'cURL' },
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
] as const;
type Lang3 = (typeof LANG3)[number]['id'];

/** cURL/Python/Node tabs. */
function Code3({ snippets }: { snippets: Record<Lang3, string> }) {
  const [lang, setLang] = useState<Lang3>('python');
  return <CodeBlock code={snippets[lang]} tabs={<Tabs tight tabs={LANG3} value={lang} onChange={setLang} />} />;
}

const yes = (note: string) => (
  <>
    <b>Ya</b> · {note}
  </>
);

/* Format Anthropic */

export function AnthropicPage() {
  const { model } = useDocs();
  return (
    <>
      <PageHead slug="anthropic">
        Selain format OpenAI, Partner AI menerima format Anthropic Messages. Claude Code dan SDK resmi Anthropic bisa langsung dipakai,
        untuk model Claude maupun model lain.
      </PageHead>

      <DocSection id="endpoint" title="Endpoint">
        <EndpointList
          items={[
            { method: 'POST', path: '/v1/messages', note: 'Kirim pesan, sinkron atau streaming' },
            { method: 'POST', path: '/v1/messages/count_tokens', note: 'Perkiraan jumlah token, gratis' },
            { method: 'GET', path: '/v1/models', note: 'Daftar model, terbaca SDK Anthropic' },
          ]}
        />
        <p>
          Kirim API key di header <code>x-api-key</code> atau <code>Authorization: Bearer</code>. Header <code>anthropic-version</code>{' '}
          boleh dikirim dan tidak wajib. Base URL untuk SDK adalah <code>{API_ORIGIN}</code>, tanpa <code>/v1</code>.
        </p>
      </DocSection>

      <DocSection id="contoh" title="Contoh request">
        <Code3
          snippets={{
            curl: [
              `curl ${API_ORIGIN}/v1/messages \\`,
              `  -H "x-api-key: $${API_KEY_ENV}" \\`,
              '  -H "anthropic-version: 2023-06-01" \\',
              '  -H "content-type: application/json" \\',
              `  -d '{"model": "${model}", "max_tokens": 1024, "messages": [{"role": "user", "content": "Halo!"}]}'`,
            ].join('\n'),
            python: [
              '# pip install anthropic',
              'import os',
              'import anthropic',
              '',
              `client = anthropic.Anthropic(base_url="${API_ORIGIN}", api_key=os.environ["${API_KEY_ENV}"])`,
              '',
              'message = client.messages.create(',
              `    model="${model}",`,
              '    max_tokens=1024,',
              '    messages=[{"role": "user", "content": "Jelaskan Kubernetes dalam dua kalimat."}],',
              ')',
              'print(message.content[0].text)',
            ].join('\n'),
            node: [
              '// npm install @anthropic-ai/sdk',
              "import Anthropic from '@anthropic-ai/sdk';",
              '',
              `const client = new Anthropic({ baseURL: '${API_ORIGIN}', apiKey: process.env.${API_KEY_ENV} });`,
              '',
              'const message = await client.messages.create({',
              `  model: '${model}',`,
              '  max_tokens: 1024,',
              "  messages: [{ role: 'user', content: 'Jelaskan Kubernetes dalam dua kalimat.' }],",
              '});',
              'console.log(message.content[0].text);',
            ].join('\n'),
          }}
        />
      </DocSection>

      <DocSection id="fitur" title="Yang didukung">
        <DocTable
          head={['Fitur', 'Status']}
          rows={[
            { key: 'stream', cells: ['Streaming (message_start, content_block_delta, message_stop)', yes('semua model')] },
            { key: 'tools', cells: ['tool_use, tool_result, tool_choice', yes('model dengan tool calling')] },
            { key: 'thinking', cells: [<code>thinking</code>, yes('adaptive dan budget_tokens, blok bertanda tangan dikirim balik utuh')] },
            { key: 'media', cells: ['Gambar dan dokumen PDF', yes('Claude, GPT, dan Gemini')] },
            { key: 'system', cells: ['Pesan system di tengah percakapan', yes('dipakai Claude Code versi baru')] },
            { key: 'output', cells: [<code>output_config</code>, yes('effort dan format json_schema')] },
            { key: 'others', cells: ['Model selain Claude', yes('format diterjemahkan otomatis')] },
          ]}
        />
      </DocSection>

      <DocSection id="caching" title="Prompt caching">
        <p>
          Tandai bagian prompt yang berulang dengan <code>cache_control: {'{"type": "ephemeral"}'}</code> di system, tools, atau pesan.
          Untuk model Claude, bagian itu disimpan sementara. Request berikutnya yang memakai awalan sama ditagih dengan tarif
          <i> cached input</i> yang jauh lebih murah (lihat <DocLink slug="model" hash="katalog">daftar model</DocLink>). Menulis cache
          pertama kali dihitung sedikit lebih mahal dari input biasa.
        </p>
        <p>
          Maksimal empat penanda per request. Kalau lebih, penanda paling awal dilepas. Model lain (GPT, DeepSeek, Gemini) memakai cache
          otomatis dari providernya, sehingga <code>cache_control</code> diabaikan.
        </p>
      </DocSection>

      <DocSection id="batasan" title="Batasan">
        <ul>
          <li>Tool server Anthropic (web search, web fetch, code execution) diabaikan karena berjalan di server Anthropic.</li>
          <li>Files API, Message Batches, dan Skills API belum tersedia.</li>
          <li>
            Header <code>anthropic-beta</code> dan field yang tidak dikenal diabaikan. Daftarnya dikirim balik di header{' '}
            <code>x-aiagg-ignored-params</code>.
          </li>
        </ul>
        <Callout>
          Error memakai bentuk Anthropic, misalnya <code>{'{"type": "error", "error": {"type": "billing_error", ...}}'}</code> untuk saldo
          habis (402). Kode lengkap ada di <DocLink slug="error" />.
        </Callout>
      </DocSection>
    </>
  );
}

/* Responses API */

export function ResponsesPage() {
  const { model } = useDocs();
  return (
    <>
      <PageHead slug="responses">
        Format Responses dari OpenAI, dipakai Codex dan OpenAI Agents SDK. Bisa memanggil semua model teks di Partner AI.
      </PageHead>

      <DocSection id="endpoint" title="Endpoint">
        <EndpointList
          items={[
            { method: 'POST', path: '/v1/responses', note: 'Buat respons, sinkron atau streaming' },
            { method: 'POST', path: '/v1/responses/input_tokens', note: 'Perkiraan jumlah token, gratis' },
          ]}
        />
      </DocSection>

      <DocSection id="contoh" title="Contoh request">
        <Code3
          snippets={{
            curl: [
              `curl ${BASE_URL}/responses \\`,
              `  -H "Authorization: Bearer $${API_KEY_ENV}" \\`,
              '  -H "content-type: application/json" \\',
              `  -d '{"model": "${model}", "input": "Tulis haiku tentang hujan."}'`,
            ].join('\n'),
            python: [
              'import os',
              'from openai import OpenAI',
              '',
              `client = OpenAI(base_url="${BASE_URL}", api_key=os.environ["${API_KEY_ENV}"])`,
              '',
              `response = client.responses.create(model="${model}", input="Tulis haiku tentang hujan.")`,
              'print(response.output_text)',
            ].join('\n'),
            node: [
              "import OpenAI from 'openai';",
              '',
              `const client = new OpenAI({ baseURL: '${BASE_URL}', apiKey: process.env.${API_KEY_ENV} });`,
              '',
              `const response = await client.responses.create({ model: '${model}', input: 'Tulis haiku tentang hujan.' });`,
              'console.log(response.output_text);',
            ].join('\n'),
          }}
        />
      </DocSection>

      <DocSection id="fitur" title="Yang didukung">
        <DocTable
          head={['Fitur', 'Status']}
          rows={[
            { key: 'input', cells: [<>input teks atau item, <code>instructions</code>, pesan developer</>, yes('semua model')] },
            { key: 'stream', cells: ['Streaming (response.created sampai response.completed)', yes('semua model')] },
            { key: 'tools', cells: ['Tool function, custom, dan namespace', yes('model dengan tool calling')] },
            { key: 'reasoning', cells: [<code>reasoning</code>, yes('effort, dan ringkasan berpikir sebagai item reasoning')] },
            { key: 'media', cells: ['input_image dan input_file (PDF)', yes('model yang menerimanya')] },
            { key: 'json', cells: [<code>text.format</code>, yes('json_object dan json_schema')] },
          ]}
        />
      </DocSection>

      <DocSection id="batasan" title="Batasan">
        <ul>
          <li>
            Respons tidak disimpan. <code>previous_response_id</code> dan <code>background</code> ditolak. Kirim seluruh percakapan di{' '}
            <code>input</code>, seperti yang dilakukan Codex.
          </li>
          <li>Tool hosted OpenAI (web search, file search, code interpreter, computer use, image generation) diabaikan.</li>
          <li>Reasoning terenkripsi tidak dikirim. Ringkasan berpikir tetap ada bila modelnya menyediakan.</li>
        </ul>
      </DocSection>
    </>
  );
}

/* Embeddings */

const EMBED_PARAMS: Param[] = [
  { name: 'model', type: 'string', required: true, desc: 'ID model embedding, lihat tabel di bawah.' },
  { name: 'input', type: 'string | string[]', required: true, desc: 'Teks yang diubah jadi vektor. Maksimal 2048 teks per request, tidak boleh kosong.' },
  { name: 'dimensions', type: 'integer', desc: 'Panjang vektor yang lebih pendek, bila modelnya mendukung.' },
  { name: 'encoding_format', type: '"float" | "base64"', desc: 'Bawaan float. SDK OpenAI otomatis meminta base64 lalu mengubahnya kembali.' },
];

export function EmbeddingsPage() {
  const { models } = useDocs();
  const embed = pick(models, 'embedding', 'text-embedding-3-small');
  const id = embed?.id ?? 'text-embedding-3-small';
  const list = (models ?? []).filter((m) => m.modality === 'embedding' && m.owned_by !== 'mock');

  return (
    <>
      <PageHead slug="embeddings">
        Ubah teks jadi vektor angka untuk pencarian semantik, RAG, dan indeks kode di Continue atau alat sejenis.
      </PageHead>

      <DocSection id="request" title="Kirim request">
        <EndpointList items={[{ method: 'POST', path: '/v1/embeddings', note: 'Bentuk sama dengan OpenAI' }]} />
        <Code3
          snippets={{
            curl: [
              `curl ${BASE_URL}/embeddings \\`,
              `  -H "Authorization: Bearer $${API_KEY_ENV}" \\`,
              '  -H "content-type: application/json" \\',
              `  -d '{"model": "${id}", "input": ["kopi susu gula aren", "es teh manis"]}'`,
            ].join('\n'),
            python: [
              'import os',
              'from openai import OpenAI',
              '',
              `client = OpenAI(base_url="${BASE_URL}", api_key=os.environ["${API_KEY_ENV}"])`,
              '',
              `result = client.embeddings.create(model="${id}", input=["kopi susu gula aren", "es teh manis"])`,
              'print(len(result.data[0].embedding))',
            ].join('\n'),
            node: [
              "import OpenAI from 'openai';",
              '',
              `const client = new OpenAI({ baseURL: '${BASE_URL}', apiKey: process.env.${API_KEY_ENV} });`,
              '',
              `const result = await client.embeddings.create({ model: '${id}', input: ['kopi susu gula aren', 'es teh manis'] });`,
              'console.log(result.data[0].embedding.length);',
            ].join('\n'),
          }}
        />
      </DocSection>

      <DocSection id="parameter" title="Parameter">
        <ParamTable params={EMBED_PARAMS} />
      </DocSection>

      <DocSection id="respons" title="Respons">
        <CodeBlock
          label="JSON"
          code={pretty({
            object: 'list',
            data: [{ object: 'embedding', index: 0, embedding: [0.0123, -0.0456, 0.0789, '…'] }],
            model: id,
            usage: { prompt_tokens: 12, total_tokens: 12 },
            billing: { currency: 'IDR', amount: 0.01, pricing_version_id: '…' },
          })}
        />
        <p>Embeddings hanya ditagih untuk token masukan.</p>
      </DocSection>

      <DocSection id="model" title="Model embedding">
        {list.length ? (
          <DocTable
            head={['Model', 'Dimensi', 'Harga per 1 juta token']}
            rows={list.map((m) => ({
              key: m.id,
              cells: [<code>{m.id}</code>, m.max_output_tokens ? String(m.max_output_tokens) : '—', m.pricing?.input_per_1m !== undefined ? idr(round2(m.pricing.input_per_1m), true) : '—'],
            }))}
          />
        ) : (
          <p>
            Daftar lengkap ada di <DocLink slug="model" hash="katalog">daftar model</DocLink>.
          </p>
        )}
      </DocSection>
    </>
  );
}

/* Completions */

const COMPLETION_PARAMS: Param[] = [
  { name: 'model', type: 'string', required: true, desc: 'ID model teks mana pun.' },
  { name: 'prompt', type: 'string', required: true, desc: 'Teks sebelum kursor, atau teks yang ingin dilanjutkan.' },
  { name: 'suffix', type: 'string', desc: 'Teks setelah kursor. Bila diisi, model mengisi bagian tengahnya (fill in the middle).' },
  { name: 'max_tokens', type: 'integer', desc: 'Bawaan 16, sama seperti OpenAI. Naikkan untuk saran yang lebih panjang.' },
  { name: 'stream / stream_options', type: 'boolean / object', desc: <>Streaming SSE. <code>include_usage</code> menambah frame pemakaian di akhir.</> },
  { name: 'temperature / top_p / stop', type: 'number / string[]', desc: 'Sama dengan chat.' },
];

export function CompletionsPage() {
  const { model } = useDocs();
  return (
    <>
      <PageHead slug="completions">
        Endpoint completions versi lama untuk plugin autocomplete dan kode lama. Untuk proyek baru, pakai{' '}
        <DocLink slug="chat">chat completions</DocLink>.
      </PageHead>

      <DocSection id="request" title="Kirim request">
        <EndpointList items={[{ method: 'POST', path: '/v1/completions', note: 'Bentuk text_completion OpenAI' }]} />
        <Code3
          snippets={{
            curl: [
              `curl ${BASE_URL}/completions \\`,
              `  -H "Authorization: Bearer $${API_KEY_ENV}" \\`,
              '  -H "content-type: application/json" \\',
              `  -d '{"model": "${model}", "prompt": "def tambah(a, b):\\n    ", "suffix": "\\n\\nprint(tambah(1, 2))", "max_tokens": 64}'`,
            ].join('\n'),
            python: [
              'import os',
              'from openai import OpenAI',
              '',
              `client = OpenAI(base_url="${BASE_URL}", api_key=os.environ["${API_KEY_ENV}"])`,
              '',
              'result = client.completions.create(',
              `    model="${model}",`,
              '    prompt="def tambah(a, b):\\n    ",',
              '    suffix="\\n\\nprint(tambah(1, 2))",',
              '    max_tokens=64,',
              ')',
              'print(result.choices[0].text)',
            ].join('\n'),
            node: [
              "import OpenAI from 'openai';",
              '',
              `const client = new OpenAI({ baseURL: '${BASE_URL}', apiKey: process.env.${API_KEY_ENV} });`,
              '',
              'const result = await client.completions.create({',
              `  model: '${model}',`,
              "  prompt: 'def tambah(a, b):\\n    ',",
              "  suffix: '\\n\\nprint(tambah(1, 2))',",
              '  max_tokens: 64,',
              '});',
              'console.log(result.choices[0].text);',
            ].join('\n'),
          }}
        />
      </DocSection>

      <DocSection id="parameter" title="Parameter">
        <ParamTable params={COMPLETION_PARAMS} />
      </DocSection>

      <DocSection id="catatan" title="Catatan">
        <ul>
          <li>Request dijalankan dengan model chat yang Anda pilih, diberi instruksi untuk hanya mengisi teks di posisi kursor.</li>
          <li>Untuk autocomplete, pilih model yang cepat dan murah agar saran muncul tanpa jeda.</li>
          <li>
            <code>echo</code>, <code>logprobs</code>, dan <code>n</code> lebih dari 1 belum didukung dan dibalas 400.
          </li>
        </ul>
      </DocSection>
    </>
  );
}
